import { createRootRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import NotFoundPage from '../pages/ErrorPage';
import { useEffect } from 'react';
import axiosInstance from '../lib/axios-instance';
import { toast } from 'sonner';
import { encode } from 'js-base64';
import { envs } from '../lib/constants';

import '../globals.css';
import { useTheme } from '../components/ui/theme-provider';
import { Toaster } from '../components/ui/sonner';
import { TLanguage } from '@/components/ui/language-picker';
import { EntityType } from '../services/direct';

type TRootSearchParams = {
  language?: TLanguage;
  entityType?: EntityType;
};

export const Route = createRootRoute({
  component: Root,
  notFoundComponent: () => <NotFoundPage />,
  validateSearch: (search): TRootSearchParams => {
    const language = search.language;
    if (language && typeof language !== 'string') {
      return {
        language: 'en',
      };
    }
    return {
      language: language as TLanguage,
      entityType: Object.values(EntityType).includes(
        search.entityType as EntityType,
      )
        ? (search.entityType as EntityType)
        : undefined,
    };
  },
});

function Root() {
  const navigate = useNavigate();

  useEffect(() => {
    const reqInterceptor = axiosInstance.interceptors.request.use(
      (config) => {
        const token = sessionStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    const resInterceptor = axiosInstance.interceptors.response.use(
      (response) => {
        const message = response.data.message || response.data.msg;
        if (message) toast.success(message);
        return response;
      },
      (error) => {
        const message = error.response.data.message || error.response.data.msg;
        if (message) toast.error(message);
        //if the status is 401, redirect to login page

        if (error.response.status === 401) {
          const id = error.response.data.refundId;
          const type = error.response.data.type;
          navigate({
            to: type === 'email' ? '/payments/' + encode(id) : '/',
          });
        }

        return Promise.reject(error);
      },
    );

    return () => {
      axiosInstance.interceptors.request.eject(reqInterceptor);
      axiosInstance.interceptors.response.eject(resInterceptor);
    };
  }, []);
  const { theme } = useTheme();

  return (
    <>
      <Outlet />
      {envs.ENVIRONMENT === 'development' && (
        <TanStackRouterDevtools position='bottom-right' />
      )}
      <Toaster theme={theme} />
    </>
  );
}
